import Link from 'found/Link';
import PropTypes from 'prop-types';
import React from 'react';
import { useIntl } from 'react-intl';
import { Table } from 'reactstrap';

import BadgeActive from 'src/components/Badge/BadgeActive';
import BadgeTradePointType from 'src/components/Badge/BadgeTradePointType';
import Loading from 'src/components/Loading';
import UUID from 'src/components/UUID';
import displayUserName from 'src/util/i18n/decorators/displayUserName';

/**
 * Description
 * @param {object} props
 * @param {object} props.trader
 * @returns {React.ReactElement} - AdminTradeRuleListTrader component
 */
function AdminTradeRuleListTrader({ trader = null }) {
  const intl = useIntl();

  if (!trader) {
    return <Loading />;
  }

  const {
    tradePoint, community, user, userId,
  } = trader;
  const { uuid: tradePointId, type: tradePointType } = tradePoint;

  const tradePointBadge = <BadgeTradePointType type={tradePointType} />;
  const userName = displayUserName(intl, user, userId);

  if (user || userName) {
    const headerType = intl.formatMessage({ id: 'admin.admin_trade_rule.admin_trade_rule_show.admin_trade_rule_show_trader.user_table.header.type', defaultMessage: 'Type' });
    const headerTradePoint = intl.formatMessage({ id: 'admin.admin_trade_rule.admin_trade_rule_show.admin_trade_rule_show_trader.user_table.header.trade_point', defaultMessage: 'Trade Point' });
    const headerUser = intl.formatMessage({ id: 'admin.admin_trade_rule.admin_trade_rule_show.admin_trade_rule_show_trader.user_table.header.user', defaultMessage: 'User' });

    return (
      <Table responsive>
        <thead>
          <tr>
            <th scope="col">{headerType}</th>
            <th scope="col">{headerTradePoint}</th>
            <th scope="col">{headerUser}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              {tradePointBadge}
            </td>
            <th scope="row">
              <UUID uuid={tradePointId} />
            </th>
            <td>
              {/* Without a resolved user (e.g. System) there is no profile, email or activity. */}
              {user ? (
                <>
                  <Link to={`/admin/users/${user.uuid}`}>{userName}</Link>
                  <br />
                  {user.email}
                  <br />
                  <BadgeActive start={user.active.start} finish={user.active.finish} />
                </>
              ) : (
                <>
                  {userName}
                  <br />
                  <UUID uuid={userId} />
                </>
              )}
            </td>
          </tr>
        </tbody>
      </Table>
    );
  }

  if (community) {
    const { uuid, title, active } = community;
    const headerType = intl.formatMessage({ id: 'admin.admin_trade_rule.admin_trade_rule_show.admin_trade_rule_show_trader.community_table.header.type', defaultMessage: 'Type' });
    const headerTradePoint = intl.formatMessage({ id: 'admin.admin_trade_rule.admin_trade_rule_show.admin_trade_rule_show_trader.community_table.header.trade_point', defaultMessage: 'Trade Point' });
    const headerCommunity = intl.formatMessage({ id: 'admin.admin_trade_rule.admin_trade_rule_show.admin_trade_rule_show_trader.community_table.header.community', defaultMessage: 'Community' });

    return (
      <Table responsive>
        <thead>
          <tr>
            <th scope="col">{headerType}</th>
            <th scope="col">{headerTradePoint}</th>
            <th scope="col">{headerCommunity}</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              {tradePointBadge}
            </td>
            <th scope="row">
              <UUID uuid={tradePointId} />
            </th>
            <td>
              <Link to={`/admin/communities/${uuid}`}>
                {title}
              </Link>
              <br />
              <BadgeActive start={active.start} finish={active.finish} />
            </td>
          </tr>
        </tbody>
      </Table>
    );
  }

  const headerType = intl.formatMessage({ id: 'admin.admin_trade_rule.admin_trade_rule_show.admin_trade_rule_show_trader.trade_point_table.header.type', defaultMessage: 'Type' });
  const headerTradePoint = intl.formatMessage({ id: 'admin.admin_trade_rule.admin_trade_rule_show.admin_trade_rule_show_trader.trade_point_table.header.trade_point', defaultMessage: 'Trade Point' });

  return (
    <Table responsive>
      <thead>
        <tr>
          <th scope="col">{headerType}</th>
          <th scope="col">{headerTradePoint}</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            {tradePointBadge}
          </td>
          <th scope="row">
            <UUID uuid={tradePointId} />
          </th>
        </tr>
      </tbody>
    </Table>
  );
}

AdminTradeRuleListTrader.propTypes = {
  trader: PropTypes.shape({
    tradePoint: PropTypes.shape({
      uuid: PropTypes.string,
      type: PropTypes.string,
    }),
    community: PropTypes.shape({
      uuid: PropTypes.string,
      title: PropTypes.string,
      active: PropTypes.shape({
        start: PropTypes.number,
        finish: PropTypes.number,
      }),
    }),
    userId: PropTypes.string,
    user: PropTypes.shape({
      uuid: PropTypes.string,
      email: PropTypes.string,
      givenName: PropTypes.string,
      familyName: PropTypes.string,
      active: PropTypes.shape({
        start: PropTypes.number,
        finish: PropTypes.number,
      }),
    }),
  }),
};

export default AdminTradeRuleListTrader;
